import { supabase } from '../config/supabase';

const DEV = import.meta.env.DEV;

/* ─── Slug Generation ─── */

function generateSlug(name) {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
  const suffix = Math.random().toString(36).slice(2, 7);
  return base ? `${base}-${suffix}` : suffix;
}

/* ─── Club CRUD ─── */

/**
 * Create a new club and add the owner as a member.
 * @param {{ name: string, description?: string, websiteUrl?: string }} data
 * @param {string} ownerId
 * @returns {Promise<{ data?: object, error?: string }>}
 */
export async function createClub(data, ownerId) {
  try {
    const slug = generateSlug(data.name);

    const { data: club, error } = await supabase
      .from('clubs')
      .insert({
        owner_id: ownerId,
        name: data.name,
        slug,
        description: data.description || null,
        website_url: data.websiteUrl || null,
      })
      .select()
      .single();

    if (error) {
      if (DEV) console.error('[Club] Create failed:', error.message);
      return { error: error.message };
    }

    // Add owner as a member with 'owner' role
    await supabase
      .from('club_memberships')
      .insert({ club_id: club.id, user_id: ownerId, role: 'owner' });

    if (DEV) console.log('[Club] Created:', club.id, club.slug);
    return { data: club };
  } catch (err) {
    if (DEV) console.error('[Club] Create error:', err.message);
    return { error: err.message };
  }
}

/**
 * Regenerate a club's invite slug (invalidates old links).
 * @param {string} clubId
 * @param {string} clubName - used to generate new slug base
 * @returns {Promise<{ data?: object, error?: string }>}
 */
export async function regenerateClubSlug(clubId, clubName) {
  try {
    const newSlug = generateSlug(clubName);
    const { data, error } = await supabase
      .from('clubs')
      .update({ slug: newSlug, updated_at: new Date().toISOString() })
      .eq('id', clubId)
      .select()
      .single();

    if (error) {
      if (DEV) console.error('[Club] Slug regenerate failed:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Club] New slug:', newSlug);
    return { data };
  } catch (err) {
    if (DEV) console.error('[Club] Slug regenerate error:', err.message);
    return { error: err.message };
  }
}

/**
 * Upload a club logo to Supabase Storage and update the club record.
 * @param {string} clubId
 * @param {File} file - image file
 * @returns {Promise<{ url?: string, error?: string }>}
 */
export async function uploadClubLogo(clubId, file) {
  try {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
    const path = `${clubId}/logo.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('club-assets')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      if (DEV) console.error('[Club] Logo upload failed:', uploadError.message);
      return { error: uploadError.message };
    }

    const { data: urlData } = supabase.storage
      .from('club-assets')
      .getPublicUrl(path);

    const url = urlData?.publicUrl;
    if (!url) return { error: 'Failed to get public URL' };

    // Add cache-busting param so browsers pick up the new image
    const bustUrl = `${url}?v=${Date.now()}`;

    // Update club record with logo URL
    const { error: updateError } = await supabase
      .from('clubs')
      .update({ logo_url: bustUrl, updated_at: new Date().toISOString() })
      .eq('id', clubId);

    if (updateError) {
      if (DEV) console.error('[Club] Logo URL update failed:', updateError.message);
      return { error: updateError.message };
    }

    if (DEV) console.log('[Club] Logo uploaded:', bustUrl);
    return { url: bustUrl };
  } catch (err) {
    if (DEV) console.error('[Club] Logo upload error:', err.message);
    return { error: err.message };
  }
}

/**
 * Update club fields.
 * @param {string} clubId
 * @param {object} fields
 * @returns {Promise<{ data?: object, error?: string }>}
 */
export async function updateClub(clubId, fields) {
  try {
    const { data, error } = await supabase
      .from('clubs')
      .update({ ...fields, updated_at: new Date().toISOString() })
      .eq('id', clubId)
      .select()
      .single();

    if (error) {
      if (DEV) console.error('[Club] Update failed:', error.message);
      return { error: error.message };
    }

    return { data };
  } catch (err) {
    if (DEV) console.error('[Club] Update error:', err.message);
    return { error: err.message };
  }
}

/**
 * Get club by slug (public — works for join flow).
 * @param {string} slug
 * @returns {Promise<object|null>}
 */
export async function getClubBySlug(slug) {
  try {
    const { data, error } = await supabase
      .rpc('get_club_by_slug', { club_slug: slug });

    if (error) {
      if (DEV) console.error('[Club] Slug lookup failed:', error.message);
      return null;
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (err) {
    if (DEV) console.error('[Club] Slug lookup error:', err.message);
    return null;
  }
}

/**
 * Get all clubs a user belongs to.
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export async function getUserClubs(userId) {
  try {
    const { data, error } = await supabase
      .rpc('get_user_clubs', { uid: userId });

    if (error) {
      if (DEV) console.error('[Club] getUserClubs failed:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getUserClubs error:', err.message);
    return [];
  }
}

/* ─── Membership ─── */

/**
 * Join a club as a member.
 * @param {string} clubId
 * @param {string} userId
 * @returns {Promise<{ error?: string }>}
 */
export async function joinClub(clubId, userId, joinSource = 'invite') {
  try {
    const { error } = await supabase
      .from('club_memberships')
      .insert({ club_id: clubId, user_id: userId, role: 'member', join_source: joinSource });

    if (error) {
      if (error.code === '23505') {
        // Already a member (unique constraint)
        return { error: null };
      }
      if (DEV) console.error('[Club] Join failed:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Club] Joined club:', clubId);
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Club] Join error:', err.message);
    return { error: err.message };
  }
}

/**
 * Auto-join the platform club (Proper Miles Drivers Club).
 * Looks up the club with is_platform_club = true, then joins as member.
 */
export async function joinPlatformClub(userId) {
  try {
    const { data: club, error: lookupErr } = await supabase
      .from('clubs')
      .select('id')
      .eq('is_platform_club', true)
      .maybeSingle();

    if (lookupErr || !club) {
      if (DEV) console.error('[Club] Platform club lookup failed:', lookupErr?.message);
      return { error: lookupErr?.message || 'Platform club not found' };
    }

    return joinClub(club.id, userId, 'platform');
  } catch (err) {
    if (DEV) console.error('[Club] Platform join error:', err.message);
    return { error: err.message };
  }
}

/**
 * Leave a club.
 * @param {string} clubId
 * @param {string} userId
 * @returns {Promise<{ error?: string }>}
 */
export async function leaveClub(clubId, userId) {
  try {
    const { error } = await supabase
      .from('club_memberships')
      .delete()
      .eq('club_id', clubId)
      .eq('user_id', userId);

    if (error) {
      if (DEV) console.error('[Club] Leave failed:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Club] Left club:', clubId);
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Club] Leave error:', err.message);
    return { error: err.message };
  }
}

/**
 * Get members of a club (admin view).
 * @param {string} clubId
 * @returns {Promise<Array>}
 */
export async function getClubMembers(clubId) {
  try {
    const { data, error } = await supabase
      .rpc('get_club_members', { club_uuid: clubId });

    if (error) {
      if (DEV) console.error('[Club] getMembers failed:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getMembers error:', err.message);
    return [];
  }
}

/**
 * Members the admin added (single add, CSV import, or the marketing
 * signup widget) who don't have a Proper Miles account yet. They live in
 * club_pending_member_invitations until they sign up, at which point
 * handle_new_user converts them into real memberships. RLS limits this
 * to the club's owners/admins.
 * @param {string} clubId
 * @returns {Promise<Array<{ id, email, name, created_at }>>}
 */
export async function getPendingClubMembers(clubId) {
  try {
    const { data, error } = await supabase
      .from('club_pending_member_invitations')
      .select('id, email, name, created_at')
      .eq('club_id', clubId)
      .is('accepted_at', null)
      .order('created_at', { ascending: false });

    if (error) {
      if (DEV) console.error('[Club] getPendingMembers failed:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getPendingMembers error:', err.message);
    return [];
  }
}

/**
 * Remove a not-yet-signed-up member (deletes their pending invitation).
 * @param {string} pendingId - club_pending_member_invitations.id
 * @returns {Promise<{ error?: string }>}
 */
export async function removePendingMember(pendingId) {
  try {
    const { error } = await supabase
      .from('club_pending_member_invitations')
      .delete()
      .eq('id', pendingId);

    if (error) {
      if (DEV) console.error('[Club] Remove pending member failed:', error.message);
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Club] Remove pending member error:', err.message);
    return { error: err.message };
  }
}

/**
 * Update a member's role (owner only).
 * @param {string} membershipId
 * @param {string} newRole - 'admin' or 'member'
 * @returns {Promise<{ error?: string }>}
 */
export async function updateMemberRole(membershipId, newRole) {
  try {
    const { error } = await supabase
      .from('club_memberships')
      .update({ role: newRole })
      .eq('id', membershipId);

    if (error) {
      if (DEV) console.error('[Club] Role update failed:', error.message);
      return { error: error.message };
    }

    if (DEV) console.log('[Club] Role updated:', membershipId, '→', newRole);
    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Club] Role update error:', err.message);
    return { error: err.message };
  }
}

/**
 * Remove a member from a club (admin/owner action).
 * @param {string} clubId
 * @param {string} userId
 * @returns {Promise<{ error?: string }>}
 */
export async function removeMember(clubId, userId) {
  try {
    const { error } = await supabase
      .from('club_memberships')
      .delete()
      .eq('club_id', clubId)
      .eq('user_id', userId);

    if (error) {
      if (DEV) console.error('[Club] Remove member failed:', error.message);
      return { error: error.message };
    }

    return { error: null };
  } catch (err) {
    if (DEV) console.error('[Club] Remove member error:', err.message);
    return { error: err.message };
  }
}

/**
 * Bulk-import club members from a parsed CSV. Each row is `{ email, name? }`.
 * Calls the SECURITY DEFINER RPC `import_club_members` which:
 *   - adds memberships for emails that already match a user
 *   - stashes pending invitations for emails that don't (they auto-join
 *     when the person signs up later)
 *
 * @param {string} clubId
 * @param {Array<{ email: string, name?: string }>} rows
 * @returns {Promise<{ data?: { added, invited, already_member, skipped }, error?: string }>}
 */
export async function importClubMembers(clubId, rows) {
  try {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { error: 'No rows to import' };
    }
    const { data, error } = await supabase.rpc('import_club_members', {
      p_club_id: clubId,
      p_rows: rows,
    });
    if (error) {
      if (DEV) console.error('[Club] importClubMembers RPC failed:', error.message);
      return { error: error.message };
    }
    // RPC returns a single row of (added, invited, already_member, skipped)
    const row = Array.isArray(data) ? data[0] : data;
    return {
      data: {
        added: row?.added || 0,
        invited: row?.invited || 0,
        already_member: row?.already_member || 0,
        skipped: row?.skipped || 0,
      },
    };
  } catch (err) {
    if (DEV) console.error('[Club] importClubMembers exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Add an admin by email. Routes through a SECURITY DEFINER RPC so the
 * email lookup bypasses RLS — otherwise users from other clubs are
 * invisible to the caller and the function falsely reports "no account
 * found." The RPC re-authorizes the caller as the club's owner / admin
 * (or the platform super admin) before doing anything.
 *
 * @param {string} clubId
 * @param {string} email
 * @returns {Promise<{ data?: object, error?: string }>}
 */
export async function addAdminByEmail(clubId, email) {
  try {
    const { data, error } = await supabase.rpc('add_club_admin_by_email', {
      p_club_id: clubId,
      p_email: email,
    });

    if (error) {
      if (DEV) console.error('[Club] addAdminByEmail RPC failed:', error.message);
      // The RPC raises with the user-facing error message directly, so
      // we can forward it as-is.
      return { error: error.message };
    }

    // RPC returns a single row of (user_id, email, first_name, last_name, action).
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      return { error: 'Unexpected empty response from server.' };
    }

    if (row.action === 'already_admin') {
      return { error: 'This person is already an admin.' };
    }

    if (DEV) console.log(`[Club] ${row.action} as admin:`, row.email);
    return {
      data: {
        id: row.user_id,
        email: row.email,
        first_name: row.first_name,
        last_name: row.last_name,
      },
    };
  } catch (err) {
    if (DEV) console.error('[Club] addAdminByEmail exception:', err.message);
    return { error: err.message };
  }
}

/**
 * Get clubs owned/admin'd by a user (for admin portal).
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export async function getOwnedClubs(userId) {
  try {
    const { data, error } = await supabase
      .from('clubs')
      .select('*')
      .eq('owner_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      if (DEV) console.error('[Club] getOwnedClubs failed:', error.message);
      return [];
    }

    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getOwnedClubs error:', err.message);
    return [];
  }
}

/* ─── Platform Admin ─── */

/**
 * Get all clubs across platform (platform owner only).
 * @returns {Promise<Array>}
 */
export async function getAllClubsAdmin() {
  try {
    const { data, error } = await supabase.rpc('admin_all_clubs');
    if (error) {
      if (DEV) console.error('[Club] getAllClubsAdmin error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getAllClubsAdmin exception:', err.message);
    return [];
  }
}

/**
 * Get all routes across platform (platform owner only).
 * @returns {Promise<Array>}
 */
export async function getAllRoutesAdmin() {
  try {
    const { data, error } = await supabase.rpc('admin_all_routes');
    if (error) {
      if (DEV) console.error('[Club] getAllRoutesAdmin error:', error.message);
      return [];
    }
    return data || [];
  } catch (err) {
    if (DEV) console.error('[Club] getAllRoutesAdmin exception:', err.message);
    return [];
  }
}
